import React, { useState, useRef, useEffect, useMemo, memo } from 'react'

interface Option {
  value: string
  label: string
}

interface SearchableDropdownProps {
  id: string
  name: string
  value: string
  onChange: (value: string) => void
  options: Option[]
  placeholder: string
  disabled?: boolean
  error?: string
  required?: boolean
  className?: string
  'aria-busy'?: boolean
}

// Memoized option component for better performance
const DropdownOption = memo(({ option, onSelect }: { 
  option: Option
  onSelect: (option: Option) => void 
}) => (
  <div
    key={option.value}
    role="option"
    aria-selected={false}
    className="px-4 py-3 cursor-pointer hover:bg-gray-100 border-b border-gray-100 last:border-b-0 text-base sm:text-lg"
    onClick={() => onSelect(option)}
    onMouseDown={(e) => e.preventDefault()} // Prevent blur when clicking
  >
    {option.label}
  </div>
))

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder,
  disabled = false,
  error,
  required = false,
  className = '',
  'aria-busy': ariaBusy = false
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [displayValue, setDisplayValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Update display value when value prop changes
  useEffect(() => {
    if (value && !isOpen) { // Only update if dropdown is not open (not actively being used)
      const selectedOption = options.find(option => option.value === value)
      const newDisplayValue = selectedOption?.label || value
      // Only update if it's actually different to prevent unnecessary re-renders
      if (displayValue !== newDisplayValue) {
        setDisplayValue(newDisplayValue)
      }
      setSearchTerm('')
    } else if (!value && !isOpen) {
      setDisplayValue('')
      setSearchTerm('')
    }
  }, [value, options, isOpen, displayValue])

  // Filter options based on search term with memoization for performance
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) {
      return options
    }
    const lowerSearchTerm = searchTerm.toLowerCase()
    return options.filter(option =>
      option.label.toLowerCase().includes(lowerSearchTerm)
    )
  }, [options, searchTerm])

  // Limit rendered options for better performance - show more when searching
  const visibleOptions = useMemo(() => {
    const maxInitialOptions = searchTerm.trim() ? 100 : 25 // Reduced from 50 to 25
    return filteredOptions.slice(0, maxInitialOptions)
  }, [filteredOptions, searchTerm])

  // Handle input change (typing)
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    setDisplayValue(newValue)
    setIsOpen(true)
    
    // If the typed value exactly matches an option label or value, select it
    const exactMatch = options.find(option => 
      option.label.toLowerCase() === newValue.toLowerCase() ||
      option.value === newValue
    )
    if (exactMatch) {
      onChange(exactMatch.value)
      setDisplayValue(exactMatch.label)
      setIsOpen(false)
    } else if (!newValue) {
      onChange('')
    }
  }

  // Handle option selection
  const handleOptionSelect = (option: Option) => {
    setSearchTerm('')
    setDisplayValue(option.label)
    setIsOpen(false)
    onChange(option.value)
    inputRef.current?.blur()
  }

  // Handle input focus
  const handleFocus = () => {
    if (!disabled && !isOpen) {
      setIsOpen(true)
      // Start with current display value as search term for easy editing
      setSearchTerm(displayValue)
    }
  }

  // Handle input blur
  const handleBlur = (e: React.FocusEvent) => {
    // Don't close dropdown if clicking on an option
    if (dropdownRef.current?.contains(e.relatedTarget as Node)) {
      return
    }
    
    setTimeout(() => {
      setIsOpen(false)
      // If no exact match found, revert to selected value
      if (value) {
        const selectedOption = options.find(option => option.value === value)
        setDisplayValue(selectedOption?.label || '')
      } else {
        setDisplayValue('')
      }
      setSearchTerm('')
    }, 150)
  }

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false)
      inputRef.current?.blur()
    }
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        // Revert to selected value if no match
        if (value) {
          const selectedOption = options.find(option => option.value === value)
          setDisplayValue(selectedOption?.label || '')
        } else {
          setDisplayValue('')
        }
        setSearchTerm('')
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value, options])

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        id={id}
        name={name}
        value={isOpen ? searchTerm : displayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        required={required}
        aria-busy={ariaBusy}
        className={`w-full px-3 sm:px-4 py-2.5 sm:py-3 text-base sm:text-lg font-medium bg-white border-2 border-gray-300 rounded-md focus:border-riddell-red focus:outline-none transition-colors duration-150 ${className} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        autoComplete="off"
      />
      
      {/* Dropdown arrow */}
      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
        <svg 
          className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown options */}
      {isOpen && !disabled && (
        <div role="listbox" className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto" style={{ zIndex: 9999 }}>
          {visibleOptions.length > 0 ? (
            <>
              {visibleOptions.map((option) => (
                <DropdownOption
                  key={option.value}
                  option={option}
                  onSelect={handleOptionSelect}
                />
              ))}
              {filteredOptions.length > visibleOptions.length && (
                <div className="px-4 py-2 text-gray-500 text-sm italic border-t border-gray-200">
                  Showing {visibleOptions.length} of {filteredOptions.length} results. Type to search for more...
                </div>
              )}
            </>
          ) : (
            <div className="px-4 py-3 text-gray-500 text-base sm:text-lg">
              No matches found
            </div>
          )}
        </div>
      )}

      {/* Hidden native select for accessibility and form integration */}
      <select
        tabIndex={-1}
        className="sr-only"
        value={value}
        onChange={() => {}}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>

      {/* Error message */}
      {error && (
        <p className="mt-2 text-sm text-error font-medium flex items-center" role="alert">
          <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  )
}

export default SearchableDropdown
